import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";

const Slots = () => {
  const [slots, setSlots] = useState([]);
  const [filteredSlots, setFilteredSlots] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [expandedRows, setExpandedRows] = useState({}); // Track expanded rows

  const fetchSlots = async (
    page = 1,
    perPage = 10,
    search = "",
    client = "",
    sortBy = null,
    sortOrder = "asc"
  ) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const queryParams = new URLSearchParams({
        page,
        per_page: perPage,
      });
      if (search) queryParams.append("search", search);
      if (client) queryParams.append("client_name", client);
      if (sortBy) queryParams.append("sort_by", sortBy);
      if (sortOrder) queryParams.append("sort_order", sortOrder);

      const response = await apiCall(
        "get",
        `/get-all-slots-bo?${queryParams.toString()}`
      );

      if (response?.data?.success) {
        const slotList = response.data.data || [];
        setSlots(slotList);
        setFilteredSlots(slotList);
        setTotalEntries(response.data.total || slotList.length);
        setCurrentPage(parseInt(response.data.current_page, 10) || page);
        setEntriesPerPage(parseInt(response.data.per_page, 10) || perPage);
        setTotalPages(
          parseInt(response.data.last_page, 10) ||
            Math.ceil(response.data.total / perPage) ||
            1
        );

        const uniqueClients = [
          ...new Set(slotList.map((slot) => slot.client_name)),
        ].filter(Boolean);
        setClients(uniqueClients);
      } else {
        setErrorMessage("Failed to load slots.");
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      setErrorMessage(error.message || "Error fetching slots.");
    } finally {
      setLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedFetchSlots = useCallback(
    debounce((page, perPage, search, client, sortBy, sortOrder) => {
      fetchSlots(page, perPage, search, client, sortBy, sortOrder);
    }, 500),
    []
  );

  useEffect(() => {
    fetchSlots(currentPage, entriesPerPage, searchTerm, selectedClient, sortConfig.field, sortConfig.direction);
  }, [currentPage, entriesPerPage, sortConfig.field, sortConfig.direction]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    debouncedFetchSlots(1, entriesPerPage, value, selectedClient, sortConfig.field, sortConfig.direction);
  };

  const handleClientChange = (clientName) => {
    setSelectedClient(clientName);
    setCurrentPage(1);
    fetchSlots(1, entriesPerPage, searchTerm, clientName, sortConfig.field, sortConfig.direction);
  };

  const handleEntriesPerPageChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
    fetchSlots(1, value, searchTerm, selectedClient, sortConfig.field, sortConfig.direction);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1)
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      else if (endPage === totalPages)
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // Truncate text to a specified length
  const truncateText = (text, maxLength = 50) => {
    const textStr = String(text || "N/A"); // Convert to string, handle null/undefined
    if (textStr.length <= maxLength) return textStr;
    return textStr.substring(0, maxLength) + "...";
  };

  // Toggle expanded state for a row and field
  const toggleExpand = (slotId, field) => {
    setExpandedRows((prev) => ({
      ...prev,
      [`${slotId}_${field}`]: !prev[`${slotId}_${field}`],
    }));
  };

  const startEntry =
    totalEntries > 0 ? (currentPage - 1) * entriesPerPage + 1 : 0;
  const endEntry = Math.min(currentPage * entriesPerPage, totalEntries);

  return (
    <div className="p-4 p-md-3 p-sm-2">
      <style>
        {`
          .table-responsive .table td, .table-responsive .table th {
            max-width: 200px;
            vertical-align: middle;
          }
          .show-more-btn {
            color: #475EAB;
            cursor: pointer;
            font-size: 12px;
            margin-left: 5px;
            background: none;
            border: none;
            padding: 0;
          }
          .show-more-btn:hover {
            text-decoration: underline;
          }
          @media (max-width: 576px) {
            .table-responsive .table td, .table-responsive .table th {
              max-width: 100px;
            }
            .show-more-btn {
              fontSize: 10px;
            }
          }
        `}
      </style>
      <div className="card shadow-sm">
        <div className="card-body p-3 p-sm-2">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div className="icon-field flex-grow-1" style={{ maxWidth: "200px" }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by client name..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <div className="dropdown">
              <button
                className="btn not-active btn-sm px-3 d-flex align-items-center"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{ backgroundColor: "#475EAB", color: "white" }}
              >
                {entriesPerPage}
                <Icon
                  icon="ep:arrow-down"
                  className="ms-2"
                  style={{ fontSize: "12px", color: "white" }}
                />
              </button>
              <ul className="dropdown-menu">
                {[10, 20, 30, 40, 50].map((value) => (
                  <li key={value}>
                    <button
                      className="dropdown-item"
                      onClick={() => handleEntriesPerPageChange(value)}
                    >
                      {value}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="dropdown">
              <button
                className="btn not-active btn-sm px-3 d-flex align-items-center"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{ backgroundColor: "#475EAB", color: "white" }}
              >
                {selectedClient || "Select Client"}
                <Icon
                  icon="ep:arrow-down"
                  className="ms-2"
                  style={{ fontSize: "12px", color: "white" }}
                />
              </button>
              <ul className="dropdown-menu">
                <li>
                  <Link
                    className="dropdown-item"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleClientChange("");
                    }}
                  >
                    All Clients
                  </Link>
                </li>
                {clients.map((client, index) => (
                  <li key={index}>
                    <Link
                      className="dropdown-item"
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleClientChange(client);
                      }}
                    >
                      {client}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          {loading ? (
            <div className="text-center my-4">
              <LoadingComponent />
            </div>
          ) : filteredSlots.length === 0 ? (
            <div className="alert alert-warning" role="alert">
              No slots found.
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table bordered-table mb-0 table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("slot_id")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Slot ID</span>
                          <span className="d-flex flex-column ms-2">
                            <Icon
                              icon="ep:arrow-up"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "slot_id" &&
                                  sortConfig.direction === "asc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                opacity: 1,
                              }}
                            />
                            <Icon
                              icon="ep:arrow-down"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "slot_id" &&
                                  sortConfig.direction === "desc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                marginTop: "-4px",
                                opacity: 1,
                              }}
                            />
                          </span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("slot_number")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Slot Number</span>
                          <span className="d-flex flex-column ms-2">
                            <Icon
                              icon="ep:arrow-up"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "slot_number" &&
                                  sortConfig.direction === "asc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                opacity: 1,
                              }}
                            />
                            <Icon
                              icon="ep:arrow-down"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "slot_number" &&
                                  sortConfig.direction === "desc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                marginTop: "-4px",
                                opacity: 1,
                              }}
                            />
                          </span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("slot_type")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Slot Type</span>
                          <span className="d-flex flex-column ms-2">
                            <Icon
                              icon="ep:arrow-up"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "slot_type" &&
                                  sortConfig.direction === "asc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                opacity: 1,
                              }}
                            />
                            <Icon
                              icon="ep:arrow-down"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "slot_type" &&
                                  sortConfig.direction === "desc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                marginTop: "-4px",
                                opacity: 1,
                              }}
                            />
                          </span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("client_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Client Name</span>
                          <span className="d-flex flex-column ms-2">
                            <Icon
                              icon="ep:arrow-up"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "client_name" &&
                                  sortConfig.direction === "asc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                opacity: 1,
                              }}
                            />
                            <Icon
                              icon="ep:arrow-down"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "client_name" &&
                                  sortConfig.direction === "desc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                marginTop: "-4px",
                                opacity: 1,
                              }}
                            />
                          </span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("branch_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Branch Name</span>
                          <span className="d-flex flex-column ms-2">
                            <Icon
                              icon="ep:arrow-up"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "branch_name" &&
                                  sortConfig.direction === "asc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                opacity: 1,
                              }}
                            />
                            <Icon
                              icon="ep:arrow-down"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "branch_name" &&
                                  sortConfig.direction === "desc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                marginTop: "-4px",
                                opacity: 1,
                              }}
                            />
                          </span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("location_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Location Name</span>
                          <span className="d-flex flex-column ms-2">
                            <Icon
                              icon="ep:arrow-up"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "location_name" &&
                                  sortConfig.direction === "asc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                opacity: 1,
                              }}
                            />
                            <Icon
                              icon="ep:arrow-down"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "location_name" &&
                                  sortConfig.direction === "desc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                marginTop: "-4px",
                                opacity: 1,
                              }}
                            />
                          </span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("is_active")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Status</span>
                          <span className="d-flex flex-column ms-2">
                            <Icon
                              icon="ep:arrow-up"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "is_active" &&
                                  sortConfig.direction === "asc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                opacity: 1,
                              }}
                            />
                            <Icon
                              icon="ep:arrow-down"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "is_active" &&
                                  sortConfig.direction === "desc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                marginTop: "-4px",
                                opacity: 1,
                              }}
                            />
                          </span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("created_by")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created By</span>
                          <span className="d-flex flex-column ms-2">
                            <Icon
                              icon="ep:arrow-up"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "created_by" &&
                                  sortConfig.direction === "asc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                opacity: 1,
                              }}
                            />
                            <Icon
                              icon="ep:arrow-down"
                              style={{
                                fontSize: "12px",
                                color:
                                  sortConfig.field === "created_by" &&
                                  sortConfig.direction === "desc"
                                    ? "#ffffff"
                                    : "#D3D3D3",
                                marginTop: "-4px",
                                opacity: 1,
                              }}
                            />
                          </span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSlots.map((slot, index) => (
                      <tr key={slot.slot_id || index}>
                        <td className="p-2">{slot.slot_id ?? "N/A"}</td>
                        <td className="p-2">
                          {expandedRows[`${slot.slot_id}_slot_number`]
                            ? slot.slot_number ?? "N/A"
                            : truncateText(slot.slot_number)}
                          {slot.slot_number && String(slot.slot_number).length > 50 && (
                            <button
                              className="show-more-btn"
                              onClick={() => toggleExpand(slot.slot_id, "slot_number")}
                            >
                              {expandedRows[`${slot.slot_id}_slot_number`]
                                ? "Show Less"
                                : "Show More"}
                            </button>
                          )}
                        </td>
                        <td className="p-2">
                          {expandedRows[`${slot.slot_id}_slot_type`]
                            ? slot.slot_type ?? "N/A"
                            : truncateText(slot.slot_type)}
                          {slot.slot_type && String(slot.slot_type).length > 50 && (
                            <button
                              className="show-more-btn"
                              onClick={() => toggleExpand(slot.slot_id, "slot_type")}
                            >
                              {expandedRows[`${slot.slot_id}_slot_type`]
                                ? "Show Less"
                                : "Show More"}
                            </button>
                          )}
                        </td>
                        <td className="p-2">
                          <Link to={`/all-clients`} className="text-primary-600">
                            {expandedRows[`${slot.slot_id}_client_name`]
                              ? slot.client_name ?? "N/A"
                              : truncateText(slot.client_name)}
                          </Link>
                          {slot.client_name && String(slot.client_name).length > 50 && (
                            <button
                              className="show-more-btn"
                              onClick={() => toggleExpand(slot.slot_id, "client_name")}
                            >
                              {expandedRows[`${slot.slot_id}_client_name`]
                                ? "Show Less"
                                : "Show More"}
                            </button>
                          )}
                        </td>
                        <td className="p-2">
                          {expandedRows[`${slot.slot_id}_branch_name`]
                            ? slot.branch_name ?? "N/A"
                            : truncateText(slot.branch_name)}
                          {slot.branch_name && String(slot.branch_name).length > 50 && (
                            <button
                              className="show-more-btn"
                              onClick={() => toggleExpand(slot.slot_id, "branch_name")}
                            >
                              {expandedRows[`${slot.slot_id}_branch_name`]
                                ? "Show Less"
                                : "Show More"}
                            </button>
                          )}
                        </td>
                        <td className="p-2">
                          {expandedRows[`${slot.slot_id}_location_name`]
                            ? slot.location_name ?? "N/A"
                            : truncateText(slot.location_name)}
                          {slot.location_name && String(slot.location_name).length > 50 && (
                            <button
                              className="show-more-btn"
                              onClick={() => toggleExpand(slot.slot_id, "location_name")}
                            >
                              {expandedRows[`${slot.slot_id}_location_name`]
                                ? "Show Less"
                                : "Show More"}
                            </button>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          <span
                            className={`px-3 py-1 radius-4 fw-medium text-sm ${
                              slot.is_active
                                ? "bg-success-focus text-success-600 border border-success-main"
                                : "bg-neutral-200 text-neutral-600 border border-neutral-400"
                            }`}
                          >
                            {slot.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="p-2">
                          {expandedRows[`${slot.slot_id}_created_by`]
                            ? slot.created_by ?? "N/A"
                            : truncateText(slot.created_by)}
                          {slot.created_by && String(slot.created_by).length > 50 && (
                            <button
                              className="show-more-btn"
                              onClick={() => toggleExpand(slot.slot_id, "created_by")}
                            >
                              {expandedRows[`${slot.slot_id}_created_by`]
                                ? "Show Less"
                                : "Show More"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 gap-3">
                <span>
                  Showing {startEntry} to {endEntry} of {totalEntries} entries
                </span>
                <ul
                  className="pagination m-0 d-flex flex-wrap gap-2"
                  style={{ border: "none", background: "none" }}
                >
                  <li
                    className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      style={{
                        backgroundColor: "#a7b7ef",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "30px",
                        height: "30px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      <Icon
                        icon="ep:arrow-left"
                        style={{ fontSize: "16px", color: "white" }}
                      />
                    </button>
                  </li>
                  {getPageNumbers().map((page) => (
                    <li
                      key={page}
                      className={`page-item ${
                        currentPage === page ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        style={{
                          backgroundColor:
                            currentPage === page ? "#475EAB" : "#F5F5F5",
                          color: currentPage === page ? "white" : "#475EAB",
                          border: "none",
                          borderRadius: "50%",
                          width: "30px",
                          height: "30px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      style={{
                        backgroundColor: "#a7b7ef",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "30px",
                        height: "30px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      <Icon
                        icon="ep:arrow-right"
                        style={{ fontSize: "16px", color: "white" }}
                      />
                    </button>
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Slots;
